import { useNavigate } from 'react-router-dom';
import { theme } from '../theme.js';
import { useCoverRequests, useFaults } from '../lib/queries.js';
import { useOnNow, whoLabel } from '../lib/onnow.js';
import { KIND_LABEL } from '../lib/shifts.js';
import { Icon } from './Icon.js';
import { respondingLine } from './OffRun.js';

/** Home: who's on right now (and what's off run) in one glance; tap for the full board. */
export function OnNowCard({ orgId }: { orgId: string }) {
  const nav = useNavigate();
  const { current, next, isLoading } = useOnNow(orgId);
  const { data: open = [] } = useCoverRequests(orgId, 'open');
  const gaps = open.filter((r) => r.gaps.length > 0 && new Date(r.startsAt).getTime() < Date.now() + 48 * 3600_000).length;
  const { data: faults } = useFaults(orgId);
  const offRun = faults?.offRun ?? [];
  const responding = faults?.responding;
  if (isLoading || (!current.length && !next.length && !offRun.length)) return null;
  const show = current.length ? current : next;
  const warn = show.find((s) => s.capability?.warnings.some((w) => w.startsWith('Crew short')));
  return (
    <button onClick={() => nav('/now')} style={{
      margin: '12px 16px 0', padding: '12px 14px', borderRadius: 12, width: 'calc(100% - 32px)', textAlign: 'left',
      background: theme.surface, border: `1px solid ${warn || offRun.length ? theme.status.danger.dot : theme.border}`, cursor: 'pointer', fontFamily: theme.font,
      display: 'block', boxSizing: 'border-box',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: theme.textMuted, flex: 1 }}>
          {current.length || !next.length ? 'ON NOW' : `NEXT · ${new Date(next[0]!.startsAt).toLocaleString(undefined, { weekday: 'short', hour: 'numeric' })}`}
        </span>
        <Icon name="chevron" size={14} style={{ color: theme.textMuted }} />
      </div>
      {show.filter((s) => s.kind !== 'activity').map((s) => (
        <div key={s.id} style={{ display: 'flex', gap: 8, fontSize: 13, padding: '1px 0' }}>
          <span style={{ width: 84, flexShrink: 0, fontWeight: 700, color: theme.text }}>{KIND_LABEL[s.kind]}</span>
          <span style={{ color: theme.textDim, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{whoLabel(s)}</span>
        </div>
      ))}
      {offRun.map((a) => (
        <div key={a.assetId} style={{ fontSize: 12, fontWeight: 700, color: theme.status.danger.fg, marginTop: 4 }}>{a.assetName} is off run · {a.reason}</div>
      ))}
      {responding && (responding.isBackup || !responding.assetId) && (
        <div style={{ fontSize: 13, fontWeight: 800, color: theme.status.danger.fg, marginTop: 4 }}>{respondingLine(responding)}</div>
      )}
      {warn && <div style={{ fontSize: 12, fontWeight: 700, color: theme.status.danger.fg, marginTop: 4 }}>{warn.capability!.warnings[0]}</div>}
      {gaps > 0 && <div style={{ fontSize: 12, fontWeight: 700, color: theme.status.warn.fg, marginTop: 4 }}>{gaps} cover request{gaps > 1 ? 's' : ''} still open in the next 48h</div>}
    </button>
  );
}
